/**
 * ENG-8265 — the outstanding-interim-promise predicate.
 *
 * ## The failure this closes
 *
 * An agent that replies *"On it — pulling your v10 first…"* and then never
 * delivers trips **nothing**. `agent_stall` only selects cards with
 * `status = 'in_progress'` and the agent had none; `agent_pending_inbound`
 * measures marker age from the marker's mtime, which the interim ack's own
 * in-place rewrite advances; and `agent_channel_silent_loss` never measured
 * "did a reply go out" at all (it counts `ChannelDeflections Cause=replay_orphaned`
 * — a recoverable inbound GC'd before the replay scanner re-pushed it, which an
 * interim ack neither causes nor suppresses). So the one failure a customer
 * noticed by hand was the one the platform could not see.
 *
 * ## Why `seen_at` is NOT the signal
 *
 * ENG-6567 already stopped an interim ack from DELETING the pending marker: it
 * downgrades it in place by stamping `seen_at`, so the Stop-hook ghost-reply
 * recovery stays armed for the eventual answer. It is tempting to read a
 * lingering `seen_at` marker as an outstanding promise. **It is not.**
 * `seen_at` is stamped by every form of engagement — an interim ack, a
 * `slack.read_thread`, an emoji reaction, `noteThreadActivityByMessageTs` on a
 * reaction to any message in the thread. An agent that reads a thread, decides
 * the message is not for it and correctly says nothing owes no follow-up
 * whatsoever, and alerting on that would page a human about a conversation that
 * was fine — repeatedly, on every skipped auto-followed thread, which is exactly
 * how an alert gets trained into background noise.
 *
 * So this module keys off a DISTINCT field, `promised_at`, written by exactly one
 * caller: the channel reply tool on the `interim: true` path. `interim: true` is
 * the agent explicitly declaring "the real answer is still coming" (it is what
 * the tool's own schema says), so `promised_at` present + the marker still on
 * disk is a promise the agent made and has not discharged. A final reply
 * (`interim` omitted/false) DELETES the marker via the existing settle path —
 * including the thread fan-out, so any substantive reply anywhere in the thread
 * discharges the promise, not just one keyed to the original message_ts.
 *
 * ## Why the age is measured from `promised_at`, not from mtime
 *
 * Two reasons, both load-bearing:
 *
 *  1. **mtime is resettable by unrelated engagement.** A reaction or a
 *     `read_thread` rewrites the marker (to stamp `seen_at`) and bumps its
 *     mtime. Measuring the promise from mtime would let an agent that keeps
 *     touching the thread hold a broken promise open forever without the age
 *     ever climbing.
 *  2. **mtime does not survive the incident that motivated the ticket.**
 *     Stirling's session was killed twice mid-turn by token rotations
 *     (ENG-8264). `promised_at` is a value inside the file, so the clock keeps
 *     running across a respawn; an mtime- or session-relative measure would not.
 *
 * For the same reason this predicate deliberately does **NOT** reuse the
 * wedge-detector's `sessionStartMs` exclusion (`oldestLivePendingInboundMtimeMs`
 * skips markers older than the current session). That exclusion is correct for
 * "is THIS session failing to drain" and would have made this fix a no-op in the
 * exact incident it was filed for — stirling's promise predates both respawns.
 *
 * ## Shapes that must never count as an outstanding promise
 *
 *  - no `promised_at` (write-once marker, `seen_at`-only engagement, a
 *    read_thread, a reaction, an unrelated participant's new message)
 *  - `discretionary: true` — a skippable auto-followed participant-thread
 *    inbound the agent may legitimately never answer
 *  - `undeliverable: true` — already dead-lettered by the channel; the user got
 *    a ⏳ notice, so no promise is outstanding
 *  - a marker that no longer exists — a final reply deleted it, which is the
 *    normal ack-then-deliver path and the single most important negative case
 *  - a malformed / unparseable `promised_at` — a corrupt value must not
 *    manufacture a promise (contrast the wedge live-scan, which treats a corrupt
 *    marker as LIVE because there a corrupt marker must never MASK a real wedge;
 *    here the asymmetry runs the other way, so it fails quiet)
 *  - `promised_at` in the future (clock skew) — clamped to age 0, never negative
 */

/**
 * The subset of a channel pending-inbound marker this predicate reads. Every
 * field is optional: markers written before ENG-8265, and by channels with no
 * interim concept (direct-chat has no `interim` argument), carry none of them and
 * must parse cleanly to "no promise".
 */
export interface InterimPromiseMarkerFields {
  /**
   * ENG-8265: ISO timestamp stamped when the agent posted an INTERIM reply on
   * this inbound — an explicit "the real answer is still coming". Written only
   * by the reply tool's `interim: true` path, never by `read_thread` / reaction
   * engagement (those stamp `seen_at` only). First write wins: a second interim
   * ack on the same inbound must not restart the promise clock, or an agent that
   * keeps saying "still working" would never be visible as having dropped it.
   */
  promised_at?: unknown;
  /** ENG-6567 engagement stamp. Present for ALL engagement — not a promise. */
  seen_at?: unknown;
  /** ENG-5846: dead-lettered by the channel; the user was told, nothing owed. */
  undeliverable?: unknown;
  /** ENG-6319: skippable auto-followed inbound; a deliberate skip owes nothing. */
  discretionary?: unknown;
}

/**
 * Default promise window, in seconds. The ticket's guidance is "start generous,
 * ~30 min, tune down"; 30 min is also what the agent's own instructions imply
 * ("I'll come back shortly"), so it is the threshold that is easiest to defend.
 *
 * Shared between the CLI reporter (which needs it only for its own tests and
 * docs — CloudWatch does the comparing) and the API alarm, so the two can never
 * drift into a state where the reported age and the alarmed threshold disagree.
 */
export const DEFAULT_INTERIM_PROMISE_THRESHOLD_SECONDS = 1800;

/**
 * `promised_at` as ms-epoch when this marker carries an OUTSTANDING promise,
 * else `null`. Pure: the caller supplies the parsed marker, so this is trivially
 * exhaustible over every shape above.
 */
export function outstandingPromiseAtMs(marker: InterimPromiseMarkerFields | null | undefined): number | null {
  if (!marker || typeof marker !== 'object') return null;
  // A skip and a dead-letter both mean "nothing is owed here", regardless of
  // whether an interim ack was posted earlier.
  if (marker.discretionary === true) return null;
  if (marker.undeliverable === true) return null;
  if (typeof marker.promised_at !== 'string' || marker.promised_at === '') return null;
  const ms = Date.parse(marker.promised_at);
  // Unparseable timestamp: fail quiet. A corrupt value must not invent a promise
  // — the cost of a false alert here is a human paged about a healthy thread.
  if (!Number.isFinite(ms)) return null;
  return ms;
}

/**
 * Age (whole seconds) of a promise made at `promisedAtMs`, clamped at 0 so clock
 * skew between the marker writer and the probe can never produce a negative age
 * (which would sail under any threshold and read as healthy).
 */
export function interimPromiseAgeSeconds(promisedAtMs: number, nowMs: number): number {
  return Math.max(0, Math.floor((nowMs - promisedAtMs) / 1000));
}

/**
 * Whether a reported promise age breaches the window. Mirrors the CloudWatch
 * alarm's `GreaterThanOrEqualToThreshold` **exactly**, so a test can assert "an
 * alert by T+30min" against the same comparison the alarm performs rather than
 * against a re-derived one. `>=` (not `>`) is deliberate: the acceptance
 * criterion is "alert **by** T+30min", and with `>` a promise sitting at exactly
 * 1800s would wait for the next publish cycle.
 */
export function interimPromiseBreaches(
  ageSeconds: number,
  thresholdSeconds: number = DEFAULT_INTERIM_PROMISE_THRESHOLD_SECONDS,
): boolean {
  return Number.isFinite(ageSeconds) && ageSeconds >= thresholdSeconds;
}

/**
 * What kind of engagement is being recorded on a marker.
 *
 *  - `interim_ack` — the agent replied with `interim: true`, explicitly promising
 *    a substantive answer still to come. The ONLY kind that records a promise.
 *  - `seen` — every other form of engagement: a `read_thread`, a reaction, a
 *    deliberate skip. Proves the agent saw the inbound; promises nothing.
 */
export type MarkerEngagementKind = 'interim_ack' | 'seen';

export interface MarkerEngagementDecision {
  /** Fields to merge into the marker, or `null` when no rewrite is needed. */
  stamp: { seen_at?: string; promised_at?: string } | null;
  /**
   * Whether the marker carries an outstanding promise once this decision is
   * applied — `true` also when it was ALREADY promised and no rewrite is needed,
   * so a caller can treat "already in the desired state" as success rather than
   * as a failed write.
   */
  promised: boolean;
}

/**
 * Decide what an engagement stamps on a pending-inbound marker. Shared by the
 * Slack and Telegram channel servers so the two can never drift on the one
 * question that matters here — which engagements create a promise and which do
 * not. Pure; the caller owns reading and writing the file.
 *
 * Rules, each of which a negative test depends on:
 *
 *  - `seen` never writes `promised_at`. A read_thread or a reaction must not make
 *    a thread the agent correctly skipped look like a broken promise.
 *  - `seen` on an already-seen marker rewrites NOTHING (keeps the first
 *    engagement timestamp, and keeps a reaction storm from bumping mtime).
 *  - `interim_ack` on an already-promised marker rewrites NOTHING and reports
 *    `promised: true`. Keeping the FIRST promise timestamp is what makes the
 *    elapsed-time signal measure the whole outstanding window; restarting it on
 *    every "still working" ack would hide the drop indefinitely.
 *  - `interim_ack` on a seen-but-unpromised marker adds `promised_at` while
 *    leaving the earlier `seen_at` intact.
 */
export function decideMarkerEngagement(
  marker: InterimPromiseMarkerFields | null | undefined,
  kind: MarkerEngagementKind,
  nowIso: string,
): MarkerEngagementDecision {
  const alreadySeen = typeof marker?.seen_at === 'string' && marker.seen_at !== '';
  const alreadyPromised = typeof marker?.promised_at === 'string' && marker.promised_at !== '';

  if (kind === 'seen') {
    return alreadySeen
      ? { stamp: null, promised: alreadyPromised }
      : { stamp: { seen_at: nowIso }, promised: alreadyPromised };
  }
  if (alreadyPromised) return { stamp: null, promised: true };
  return {
    stamp: alreadySeen ? { promised_at: nowIso } : { seen_at: nowIso, promised_at: nowIso },
    promised: true,
  };
}

/**
 * Per-settle tally of interim-promise stamp attempts, as each channel server
 * records it while fanning out over the markers a reply settles.
 */
export interface PromiseStampTally {
  /** Markers the stamp op was applied to. */
  attempted: number;
  /** Of those, how many now carry `promised_at` on disk. */
  stamped: number;
}

/**
 * The sentence a reply tool appends to its result after an `interim: true` send,
 * or `null` when nothing should be said.
 *
 * Shared by the Slack and Telegram servers for the same reason
 * {@link decideMarkerEngagement} is: an interim ack that the platform failed to
 * RECORD is the very failure this ticket closes, one layer down — nothing is
 * tracking the owed reply, and no alarm will ever fire because `promised_at` was
 * never written. A bare `sent` in that case tells the agent the obligation is
 * being watched when it is not. Slack surfaced this from day one and Telegram did
 * not (it only wrote the operator-facing stderr line), which is precisely the
 * cross-channel drift this module exists to prevent — so the text lives here and
 * both channels read it.
 *
 * Three outcomes:
 *
 *  - `attempted > stamped` — a marker matched but the promise did not persist.
 *    WARN: the agent must not rely on a reminder that cannot arrive, so tell it to
 *    answer in this same turn.
 *  - `stamped > 0` — recorded. Say so, so the agent knows the final reply is still
 *    owed and is being tracked.
 *  - `attempted === 0` — no marker matched this settle (a proactive interim post,
 *    or the inbound was already discharged). Not an error, and not worth a line.
 */
export function describePromiseStampOutcome(
  tally: PromiseStampTally,
  /**
   * The channel's own reply tool (`slack.reply` / `telegram.reply`). Only the
   * success line names it — an instruction to "send a separate reply" is
   * actionable only if the agent is told which tool call discharges the promise,
   * and naming the wrong channel's tool would be worse than naming none.
   */
  replyToolName: string,
): string | null {
  if (tally.attempted > tally.stamped) {
    return 'WARNING: this was recorded as an interim ack but the outstanding-promise marker could NOT be written, so nothing is tracking that you still owe a final reply. Do not rely on a reminder — post your substantive answer in this same turn.';
  }
  if (tally.stamped > 0) {
    return `Recorded as an interim ack — your final answer is still owed and is being tracked until you send it as a separate ${replyToolName} with interim omitted.`;
  }
  return null;
}
